import { useCallback } from "react";

import { useAppSelector } from "@/store/hook";
import { RootState } from "@/store/store";
import { getUtmSource, parsePartnerDataToArray } from "@/utils/utm.utils";

interface PartnerData {
  partnerKey: string;
  text: string;
}

interface PartnerTitle {
  partnerKey: string;
  title: string;
}

interface UsePartnerMessageReturn {
  getPartnerMessage: (
    defaultMessage: string,
    partnerDataObject: Record<string, any> | null | undefined,
    keyPrefix: string
  ) => string;
  getPartnerTitle: (
    defaultTitle: string,
    childArray: Array<any> | undefined | null
  ) => string;
}

/**
 * Custom hook to handle partner-specific messaging and titles based on UTM source
 *
 * This hook encapsulates the logic for:
 * - Fetching marketing channel data from Redux
 * - Extracting UTM source from URL or marketing data
 * - Finding and returning partner-specific messages
 * - Finding and returning partner-specific titles from child arrays
 * - Falling back to default messages/titles when no partner match found
 *
 * @example
 * const { getPartnerMessage, getPartnerTitle } = usePartnerMessage();
 *
 * // For messages
 * const message = getPartnerMessage(
 *   defaultAlertMessage,
 *   alertMessagePartner,
 *   "alertMessagePartner"
 * );
 *
 * // For titles from child array
 * const title = getPartnerTitle(defaultTitle, restructuredObj.child);
 */
export const usePartnerMessage = (): UsePartnerMessageReturn => {
  // Get marketing channel data from Redux store
  const marketingChannelData = useAppSelector(
    (state: RootState) =>
      state.quote?.attributes?.marketingChannelDetails?.marketingChannelData
  );

  /**
   * Gets partner-specific message or returns default message
   * @param defaultMessage - The default message to use if no partner match
   * @param partnerDataObject - Object containing partner-specific data from CMS
   * @param keyPrefix - The prefix to filter partner data keys (e.g., "alertMessagePartner")
   * @returns Partner-specific message or default message
   */
  const getPartnerMessage = useCallback(
    (
      defaultMessage: string,
      partnerDataObject: Record<string, any> | null | undefined,
      keyPrefix: string
    ): string => {
      // Parse partner data array from CMS object
      const partnerArray = parsePartnerDataToArray<PartnerData>(
        partnerDataObject,
        keyPrefix
      );

      // Get UTM source from URL or marketing data
      const utmSource = getUtmSource(marketingChannelData);

      // Find matching partner data
      if (utmSource && partnerArray.length > 0) {
        const partnerData = partnerArray.find(
          (partner) => partner.partnerKey === utmSource
        );

        return partnerData?.text ?? defaultMessage;
      }

      return defaultMessage;
    },
    [marketingChannelData]
  );

  /**
   * Gets partner-specific title from child array or returns default title
   * @param defaultTitle - The default title to use if no partner match
   * @param childArray - Array containing partnerTitles from CMS (restructured object's child array)
   * @returns Partner-specific title or default title
   */
  const getPartnerTitle = useCallback(
    (
      defaultTitle: string,
      childArray: Array<any> | undefined | null
    ): string => {
      // Get UTM source from URL or marketing data
      const utmSource = getUtmSource(marketingChannelData);

      if (!utmSource || !childArray) {
        return defaultTitle;
      }

      // Extract partnerTitles from child array
      const partnerTitles = childArray.find((item: any) => item.partnerTitles)
        ?.partnerTitles;

      if (partnerTitles && partnerTitles.length > 0) {
        const partnerData = partnerTitles.find(
          (partner: PartnerTitle) => partner.partnerKey === utmSource
        );

        if (partnerData?.title) {
          return partnerData.title;
        }
      }

      return defaultTitle;
    },
    [marketingChannelData]
  );

  return { getPartnerMessage, getPartnerTitle };
};
